import { useEffect, useRef, useState } from 'react'
import i18n from 'live/services/i18n'

const COPIED_FEEDBACK_MS = 1200

// drives a value's tooltip: shows the value normally, briefly swaps to "Copied!" after a
// successful click-to-copy. LiveTooltip's overlay has pointer-events disabled (by design, in
// @intelie/ui), so the value itself can't be selected/copied out of the tooltip - this is the
// practical alternative.
export const useCopyToClipboard = (displayValue: string): { tooltipContent: string; handleCopy: () => void } => {
    const [copied, setCopied] = useState(false)
    const timeoutRef = useRef<number | null>(null)

    // clears any pending "revert to the value" timeout if this row/box unmounts (or re-renders away)
    // before it fires - otherwise it calls setCopied on an unmounted component
    useEffect(
        () => () => {
            if (timeoutRef.current != null) window.clearTimeout(timeoutRef.current)
        },
        []
    )

    const handleCopy = (): void => {
        if (!navigator.clipboard) return // unavailable in insecure contexts / very old browsers

        navigator.clipboard
            .writeText(displayValue)
            .then(() => {
                setCopied(true)
                if (timeoutRef.current != null) window.clearTimeout(timeoutRef.current)
                timeoutRef.current = window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS)
            })
            .catch(() => {
                // permission denied or write failed - fail silently, the tooltip still shows the value
            })
    }

    return { tooltipContent: copied ? i18n('Copied!') : displayValue, handleCopy }
}
